import {
  ComponentFactoryResolver,
  Directive,
  Injectable,
  Optional,
  ViewContainerRef,
} from '@angular/core';

import { EMPTY, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type {
  ConfigPlugins,
  EditorButton,
  IProsemirrorConfig,
  PluginsButtons,
  PluginsEvents,
} from '@core/prosemirror-legacy';
import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  BoldPlugin,
  ChecklistPlugin,
  ContextMenuPlugin,
  EmojiEditorPlugin,
  ItalicPlugin,
  LinkEditPlugin,
  ListPlugin,
  MentionInternalStrategy,
  MentionsPlugin,
  RtSchema,
  SCHEMAS,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@core/prosemirror-legacy';
import {
  MentionsStream$,
  ProsemirrorBaseConfig,
  ScrollStream$,
} from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';

import { ChecklistItemComponent } from '../components/checklist-item/checklist-item.component';
import { MentionsListService } from '../components/mentions-strategy/mentions/mentions-list.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { ContextToolbarService } from '../components/tooltips/context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';

export type ProsemirrorDefaultConfigEvents = PluginsEvents<ConfigPlugins<ProsemirrorDefaultConfig>>;

@Injectable()
export class ProsemirrorDefaultConfig extends ProsemirrorBaseConfig implements IProsemirrorConfig {
  public eventStream$ = new Subject<ProsemirrorDefaultConfigEvents>();

  private mentionsDataSource$ = this.mentionsProvider$.pipe(takeUntil(this.ngOnDestroy$));
  private scroll$ = this.scrollStream$ || EMPTY;

  constructor(
    protected mentionsProvider$: MentionsStream$,
    // This services are only used for editor mode
    protected cfr: ComponentFactoryResolver,
    protected vcRef: ViewContainerRef,
    @Optional() protected symbolPickerService?: SymbolPickerService,
    @Optional() protected toolbarService?: ToolbarService,
    @Optional() protected contextMenuService?: ContextToolbarService,
    @Optional() protected mentionsListService?: MentionsListService,
    @Optional() protected linkService?: LinkComponentService,
    @Optional() protected scrollStream$?: ScrollStream$,
  ) {
    super(cfr, vcRef);
  }

  public getSchema() {
    return SCHEMAS[RtSchema.Default](document, {
      mentionDataSource$: this.mentionsDataSource$,
    });
  }

  public getRenderPlugins() {
    return [
      new ChecklistPlugin(this.prosemirrorComponentFactory(ChecklistItemComponent)),
      new MentionsPlugin(
        this.mentionsDataSource$,
        this.mentionsListService!,
        MentionInternalStrategy.Default,
        this.scroll$,
      ),
    ];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new ContextMenuPlugin(this.contextMenuService!, this.scroll$),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),

      new BoldPlugin(),
      new ItalicPlugin(),
      new StrikethroughPlugin(),
      new UnderlinePlugin(),
      new LinkEditPlugin(this.scroll$, this.linkService!),
      new AutoLinkWrapPlugin(),
      new ListPlugin(),
    ];
  }

  public registerToolbarButtons(buttons: PluginsButtons<ConfigPlugins<ProsemirrorDefaultConfig>>) {
    const ctxButtons = [
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleLink,
    ];
    const leftButtons = [
      [
        buttons.toggleBold,
        buttons.toggleItalic,
        buttons.toggleUnderline,
        buttons.toggleBulletList,
        buttons.toggleOrderedList,
        buttons.toggleChecklist,
        buttons.toggleLink,
      ],
    ];

    const rightButtons: EditorButton[] = [buttons.toggleMentionList, buttons.toggleEmojiList];
    this.toolbarService!.setContextButtons(ctxButtons);
    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
  }
}

@Directive({
  selector: '[prosemirrorDefaultConfig]',
  providers: [ProsemirrorDefaultConfig],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorDefaultConfigDirective {}
